import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { useSeo, SITE_ORIGIN } from "@/hooks/useSeo";
import { eras, events, siteInfo } from "@/mocks/archive";
import { getCategoryName } from "@/utils/archive";

const orderedEras = eras.filter((e) => e.id !== "all");

export default function Timeline() {
  const [activeEra, setActiveEra] = useState("all");

  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "暗黑时间轴",
        url: `${SITE_ORIGIN}/timeline`,
        inLanguage: "zh-CN",
        description:
          "按纪元和年份排列的暗黑系事件时间轴，从古代文明到当代事故，逐条还原事件演变脉络。",
        isPartOf: { "@type": "WebSite", name: siteInfo.name, url: SITE_ORIGIN },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: SITE_ORIGIN },
          { "@type": "ListItem", position: 2, name: "暗黑时间轴", item: `${SITE_ORIGIN}/timeline` },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "暗黑事件时间轴条目",
        itemListElement: [...events]
          .sort((a, b) => a.year - b.year)
          .map((event, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: event.title,
            url: `${SITE_ORIGIN}/archive/${event.id}`,
          })),
      },
    ],
    []
  );

  useSeo({
    title: "暗黑时间轴 | 暗黑史事件年份排序与演变脉络",
    description:
      "暗黑时间轴按古代、中世纪、近代、现代与当代五个纪元排列暗黑系事件，从文明湮灭到技术事故，逐条标注关键节点，帮助你看懂事件从起因到结果的完整演变过程。",
    keywords: "暗黑时间轴,暗黑史时间线,黑料编年,事件年份排序,暗黑脉络梳理",
    path: "/timeline",
    jsonLd,
  });

  const grouped = orderedEras
    .filter((era) => activeEra === "all" || era.id === activeEra)
    .map((era) => ({
      era,
      items: events.filter((e) => e.era === era.id).sort((a, b) => a.year - b.year),
    }))
    .filter((group) => group.items.length > 0);

  return (
    <SiteLayout>
      <section className="w-full px-4 md:px-8 pt-28 md:pt-32 pb-10 border-b border-background-200">
        <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "暗黑时间轴" }]} />
        <h1 className="mt-6 font-heading text-3xl md:text-5xl font-black text-foreground-950">
          暗黑时间轴
        </h1>
        <p className="mt-4 max-w-3xl text-sm md:text-base leading-relaxed text-foreground-500">
          把暗黑史中的关键事件放回它们真实发生的年代里。每个纪元内部按年份升序排列，指向
          <span className="text-foreground-800"> 事件条目详情 </span>
          可继续查看该事件的完整节点拆解。选择纪元可以只看某一个时代的信息。
        </p>
      </section>

      <div className="sticky top-16 z-30 border-b border-background-200 bg-background-50/92 backdrop-blur-md">
        <div className="w-full px-4 md:px-8 py-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="mr-2 inline-flex items-center gap-1 text-xs text-foreground-500">
              <i className="ri-filter-3-line" aria-hidden="true"></i>
              切换纪元
            </span>
            {eras.map((era) => (
              <button
                key={era.id}
                type="button"
                onClick={() => setActiveEra(era.id)}
                className={`px-3.5 py-1.5 rounded-full text-xs whitespace-nowrap cursor-pointer border transition-colors ${
                  activeEra === era.id
                    ? "bg-primary-500 text-background-950 border-primary-500"
                    : "border-background-300 text-foreground-600 hover:border-primary-700 hover:text-foreground-900"
                }`}
              >
                {era.short}
              </button>
            ))}
          </div>
        </div>
      </div>

      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="space-y-16">
          {grouped.map((group) => (
            <div key={group.era.id}>
              <div className="flex items-center gap-4">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                  {group.era.name}
                </h2>
                <span className="h-px flex-1 bg-background-200"></span>
                <span className="text-xs text-foreground-500">{group.items.length} 条</span>
              </div>

              <ol className="mt-8 relative border-l-2 border-background-300 pl-6 md:pl-12 space-y-12">
                {group.items.map((event) => (
                  <li key={event.id} className="relative">
                    <span className="absolute -left-[31px] md:-left-[55px] top-2 w-3.5 h-3.5 rounded-full bg-accent-500 ring-4 ring-background-50"></span>
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="font-heading text-xl md:text-2xl font-bold text-primary-400">
                        {event.yearLabel}
                      </span>
                      <span className="px-2 py-0.5 rounded-full text-xs bg-secondary-900/40 text-secondary-200">
                        {getCategoryName(event.category)}
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs text-foreground-500">
                        <i className="ri-map-pin-line" aria-hidden="true"></i>
                        {event.location}
                      </span>
                    </div>

                    <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-6 rounded-lg border border-background-200 bg-background-100 p-5">
                      <div className="lg:col-span-1 overflow-hidden rounded-lg">
                        <div className="w-full h-40 overflow-hidden">
                          <img
                            src={event.cover}
                            alt={`${event.title} 事件档案配图`}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                      </div>
                      <div className="lg:col-span-2">
                        <h3 className="font-heading text-lg font-bold text-foreground-950">
                          <Link
                            to={`/archive/${event.id}`}
                            className="cursor-pointer hover:text-primary-400 transition-colors"
                          >
                            {event.title}
                          </Link>
                        </h3>
                        <p className="mt-2 text-sm leading-relaxed text-foreground-500">
                          {event.summary}
                        </p>
                        <ul className="mt-4 space-y-2">
                          {event.timeline.slice(0, 3).map((node) => (
                            <li key={node.date} className="flex items-start gap-2 text-xs">
                              <i className="ri-checkbox-blank-circle-fill mt-1 text-[6px] text-primary-500" aria-hidden="true"></i>
                              <span className="text-foreground-600">
                                <span className="text-foreground-800">{node.date}</span>
                                <span className="mx-2 text-foreground-600">·</span>
                                {node.title}
                              </span>
                            </li>
                          ))}
                        </ul>
                        <Link
                          to={`/archive/${event.id}`}
                          className="mt-4 inline-flex items-center gap-1 text-sm text-primary-400 hover:text-primary-300 cursor-pointer transition-colors"
                        >
                          查看完整节点
                          <i className="ri-arrow-right-line" aria-hidden="true"></i>
                        </Link>
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>

        {grouped.length === 0 && (
          <p className="py-20 text-center text-sm text-foreground-500">该纪元暂无收录档案。</p>
        )}
      </section>

      <section className="w-full px-4 md:px-8 py-14 border-t border-background-200 bg-background-100">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
              想按主题而不是年代浏览？
            </h2>
            <p className="mt-2 text-sm text-foreground-500">
              档案库支持按分类筛选与关键词检索，适合带着具体线索来找资料。
            </p>
          </div>
          <Link
            to="/archive"
            className="shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm whitespace-nowrap cursor-pointer bg-primary-500 text-background-950 hover:bg-primary-400 transition-colors"
          >
            <i className="ri-search-eye-line" aria-hidden="true"></i>
            前往档案库
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}